import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Calendar, Trash2 } from "lucide-react";
import config from "../../../config";
import { AddToCartButton } from "../homePageComponents/ButtonComponents";
import RatingStars from "../homePageComponents/RatingStars";

export default function BookWishlistItem({
  book,
  imageUrl,
  date,
  onRemove,
  isGrid = false,
}) {
  const {
    bookName: title = "Unknown Title",
    bookPublication: publisher = "Unknown Publisher",
    bookIsbn10,
    bookIsbn13,
    bookStockStatus,
    author,
  } = book;

  const isbn = bookIsbn13 || bookIsbn10;
  const inStock = bookStockStatus === "IN_STOCK";
  const authorName = author?.authorName || "Unknown Author";
  const navigate = useNavigate();

  const resolvedImageUrl =
    imageUrl && imageUrl !== "defaultImage.jpg" && imageUrl !== "/default-cover.jpg"
      ? imageUrl.startsWith("http")
        ? imageUrl
        : `${config.url}${imageUrl}`
      : "/images/defaultBook.png";

  const handleBookClick = () => {
    if (book.bookId) {
      navigate(`/book/${book.bookId}`);
    }
  };

  // 2-in-a-row Grid Card Layout (for multiple books)
  if (isGrid) {
    return (
      <div className="bg-white rounded-xl border border-gray-200/80 hover:border-[#FF7236]/40 shadow-sm hover:shadow transition-all duration-200 p-4 sm:p-5 flex flex-col justify-between gap-4 h-full">
        {/* Cover + Information */}
        <div className="flex items-start gap-3.5 sm:gap-4 flex-1 min-w-0">
          {/* Cover Image */}
          <div
            onClick={handleBookClick}
            className="cursor-pointer flex-shrink-0 group relative overflow-hidden rounded-lg border border-gray-100 shadow-sm bg-gray-50"
          >
            <img
              src={resolvedImageUrl}
              alt={`${title} cover`}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/images/defaultBook.png";
              }}
              className="w-20 h-28 sm:w-22 sm:h-30 object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>

          {/* Book Details */}
          <div className="flex-1 min-w-0 space-y-1.5">
            <h3
              onClick={handleBookClick}
              className="font-semibold text-gray-900 hover:text-primary transition-colors cursor-pointer text-sm sm:text-base line-clamp-2 leading-snug"
              title={title}
            >
              {title}
            </h3>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-600">
              {authorName && (
                <span>
                  <span className="text-gray-400">By</span>{" "}
                  <span className="font-medium text-gray-700">{authorName}</span>
                </span>
              )}
              {publisher && publisher !== "Unknown Publisher" && (
                <>
                  <span className="text-gray-300">•</span>
                  <span className="text-gray-500">{publisher}</span>
                </>
              )}
            </div>

            {isbn && isbn !== "N/A" && (
              <p className="text-xs text-gray-400">ISBN: {isbn}</p>
            )}

            {book.averageRating ? (
              <div className="flex items-center gap-1.5 pt-0.5">
                <RatingStars rating={book.averageRating} size={13} />
                <span className="text-xs font-semibold text-gray-700">
                  {Number(book.averageRating).toFixed(1)}
                </span>
              </div>
            ) : null}

            {/* Badges */}
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {inStock ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  In Stock
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                  Out of Stock
                </span>
              )}

              {date && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200/80 px-2.5 py-0.5 rounded-full">
                  <Calendar size={12} className="text-gray-400" />
                  <span>Added {date}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons (at bottom of grid card) */}
        <div className="flex items-center gap-2.5 pt-3 border-t border-gray-100 mt-auto">
          <div className="flex-1">
            <AddToCartButton
              book={{ ...book, id: book.bookId }}
              className="w-full px-3 py-2 text-xs sm:text-sm font-medium rounded-lg shadow-sm"
            />
          </div>
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${title} from wishlist`}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 border border-gray-200 hover:border-red-200 rounded-lg transition-colors group flex-shrink-0"
          >
            <Trash2 size={15} className="group-hover:scale-110 transition-transform" />
            <span>Remove</span>
          </button>
        </div>
      </div>
    );
  }

  // Single Item Layout (matching the user's screenshot)
  return (
    <div className="bg-white rounded-xl border border-gray-200/80 hover:border-[#FF7236]/40 shadow-sm hover:shadow transition-all duration-200 p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 sm:gap-6">
      {/* Book Cover + Information */}
      <div className="flex items-start gap-4 sm:gap-5 flex-1 min-w-0">
        {/* Cover Image */}
        <div
          onClick={handleBookClick}
          className="cursor-pointer flex-shrink-0 group relative overflow-hidden rounded-lg border border-gray-100 shadow-sm bg-gray-50"
        >
          <img
            src={resolvedImageUrl}
            alt={`${title} cover`}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/images/defaultBook.png";
            }}
            className="w-20 h-28 sm:w-24 sm:h-32 object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        {/* Book Details */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <h3
            onClick={handleBookClick}
            className="font-semibold text-gray-900 hover:text-primary transition-colors cursor-pointer text-base sm:text-lg line-clamp-2 leading-snug"
            title={title}
          >
            {title}
          </h3>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600">
            {authorName && (
              <span>
                <span className="text-gray-400">By</span>{" "}
                <span className="font-medium text-gray-700">{authorName}</span>
              </span>
            )}
            {publisher && publisher !== "Unknown Publisher" && (
              <>
                <span className="hidden sm:inline text-gray-300">•</span>
                <span className="hidden sm:inline text-gray-500">{publisher}</span>
              </>
            )}
          </div>

          {isbn && isbn !== "N/A" && (
            <p className="text-xs text-gray-400">ISBN: {isbn}</p>
          )}

          {/* Rating stars if available */}
          {book.averageRating ? (
            <div className="flex items-center gap-1.5 pt-0.5">
              <RatingStars rating={book.averageRating} size={14} />
              <span className="text-xs font-semibold text-gray-700">
                {Number(book.averageRating).toFixed(1)}
              </span>
            </div>
          ) : null}

          {/* Badges: Stock Status & Added Date (only if date is valid) */}
          <div className="flex flex-wrap items-center gap-2 pt-1.5">
            {inStock ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                In Stock
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                Out of Stock
              </span>
            )}

            {/* Date badge: Rendered ONLY when date is valid */}
            {date && (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200/80 px-2.5 py-0.5 rounded-full">
                <Calendar size={12} className="text-gray-400" />
                <span>Added {date}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-row md:flex-col items-center md:items-end justify-end gap-2.5 pt-3 md:pt-0 border-t md:border-t-0 border-gray-100 flex-shrink-0 w-full md:w-auto">
        <div className="w-1/2 md:w-32">
          <AddToCartButton
            book={{ ...book, id: book.bookId }}
            className="w-full px-3 py-2 text-xs sm:text-sm font-medium rounded-lg shadow-sm"
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${title} from wishlist`}
          className="inline-flex items-center justify-center gap-1.5 w-1/2 md:w-32 px-3 py-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 border border-gray-200 hover:border-red-200 rounded-lg transition-colors group"
        >
          <Trash2 size={15} className="group-hover:scale-110 transition-transform" />
          <span>Remove</span>
        </button>
      </div>
    </div>
  );
}

BookWishlistItem.propTypes = {
  book: PropTypes.shape({
    bookId: PropTypes.number,
    bookName: PropTypes.string,
    bookPublication: PropTypes.string,
    bookIsbn10: PropTypes.string,
    bookIsbn13: PropTypes.string,
    bookStockStatus: PropTypes.string,
    author: PropTypes.shape({
      authorName: PropTypes.string,
    }),
    averageRating: PropTypes.number,
    rating: PropTypes.number,
  }).isRequired,
  imageUrl: PropTypes.string.isRequired,
  date: PropTypes.string,
  onAddToCart: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
  isGrid: PropTypes.bool,
};
